import React, { useState, useEffect } from 'react';
import { feedbackAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Trash2, CheckCircle, XCircle, MessageSquare, Loader2, Star, Calendar, User } from 'lucide-react';

const AdminFeedbackDashboard = ({ onClose }) => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        approved: 0,
        pending: 0,
        avgRating: 0
    });

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const loadFeedback = async () => {
        try {
            setLoading(true);
            const response = await feedbackAPI.getAllFeedback({
                pageNumber,
                pageSize,
                sortBy: 'createdDate',
                sortOrder: 'desc'
            });

            const data = response.data;
            setFeedbacks(data.content || []);
            setTotalPages(data.totalPages || 0);

            // Calculate simple stats from the current page (ideal would be backend stats API)
            // For now, let's just show counts from pagination metadata if available or calculate from list
            setStats({
                total: data.totalElements || 0,
                // These are just estimations based on current view if backend doesn't provide aggregate stats yet
                // In a real app, we'd have a separate stats endpoint.
                approved: 0,
                pending: 0,
                avgRating: 0
            });

            setError(null);
        } catch (err) {
            console.error('Failed to load feedback:', err);
            setError('Failed to load feedback data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFeedback();
    }, [pageNumber]);

    const handleApprove = async (id) => {
        try {
            await feedbackAPI.approveFeedback(id);
            // Optimistic update
            setFeedbacks(prev => prev.map(f =>
                f.feedbackId === id ? { ...f, approvedFlg: true } : f
            ));
        } catch (err) {
            console.error('Failed to approve:', err);
            setError('Failed to approve feedback.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this feedback?')) {
            try {
                await feedbackAPI.deleteFeedbackAdmin(id);
                loadFeedback(); // Reload to refresh list
            } catch (err) {
                console.error('Failed to delete:', err);
                setError('Failed to delete feedback.');
            }
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 md:p-6 animate-in fade-in duration-300">
            <Card className="w-full max-w-6xl h-full md:h-[90vh] flex flex-col shadow-2xl bg-white/95 dark:bg-[#001524]/95 border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden backdrop-blur-md">
                <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between border-b px-4 md:px-6 py-4 bg-slate-50/50 dark:bg-slate-900/50 shrink-0 gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-gradient-to-br from-sky-100 to-blue-100 dark:from-sky-900/40 dark:to-blue-900/40 rounded-xl text-sky-600 dark:text-sky-400 shadow-sm ring-1 ring-sky-200 dark:ring-sky-800">
                            <MessageSquare className="h-5 w-5" />
                        </div>
                        <div>
                            <CardTitle className="text-lg md:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-300">
                                Feedback Management
                            </CardTitle>
                            <CardDescription className="text-xs md:text-sm">
                                Review and moderate user testimonials
                            </CardDescription>
                        </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose} className="h-9 w-9 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 absolute top-2 right-2 md:static transition-colors">
                        <span className="sr-only">Close</span>
                        <XCircle className="h-6 w-6 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" />
                    </Button>
                </CardHeader>

                <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50/30 dark:bg-[#001524]">
                    {error && (
                        <div className="bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 px-4 py-3 rounded-lg flex items-center gap-3 mb-6 shadow-sm">
                            <XCircle className="h-5 w-5 shrink-0" />
                            <span className="text-sm font-medium">{error}</span>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-sky-500" />
                            <p className="text-sm font-medium animate-pulse">Loading feedback...</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop View: Table */}
                            <div className="hidden md:block rounded-xl border bg-white dark:bg-slate-900 shadow-sm overflow-hidden ring-1 ring-slate-100 dark:ring-slate-800">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-slate-50/80 dark:bg-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                            <TableHead className="w-[200px] font-semibold">Customer</TableHead>
                                            <TableHead className="w-[140px] font-semibold">Rating</TableHead>
                                            <TableHead className="font-semibold">Message</TableHead>
                                            <TableHead className="w-[150px] font-semibold">Date</TableHead>
                                            <TableHead className="w-[140px] font-semibold">Status</TableHead>
                                            <TableHead className="text-right w-[100px] font-semibold">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {feedbacks.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} className="h-40 text-center text-muted-foreground">
                                                    <div className="flex flex-col items-center gap-2">
                                                        <MessageSquare className="h-8 w-8 text-slate-300" />
                                                        <p>No feedback found.</p>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            feedbacks.map((item) => (
                                                <TableRow key={item.feedbackId} className="group hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                                                    <TableCell>
                                                        <div className="flex flex-col">
                                                            <span className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                                                <User className="h-3.5 w-3.5 text-sky-500" />
                                                                {item.customerName}
                                                            </span>
                                                            {item.customerEmail && (
                                                                <span className="text-xs text-muted-foreground ml-5.5">{item.customerEmail}</span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex bg-amber-50 dark:bg-amber-900/10 w-fit px-2 py-1 rounded-md border border-amber-100 dark:border-amber-900/20">
                                                            {[...Array(5)].map((_, i) => (
                                                                <Star
                                                                    key={i}
                                                                    className={`h-3 w-3 ${i < item.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200 dark:text-slate-700'}`}
                                                                />
                                                            ))}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 w-full max-w-md" title={item.feedbackMessage}>
                                                            "{item.feedbackMessage}"
                                                        </p>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                                                            <Calendar className="h-3.5 w-3.5" />
                                                            {item.createdDate ? new Date(item.createdDate).toLocaleDateString() : '-'}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.approvedFlg ? (
                                                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                                                <CheckCircle className="h-3 w-3" /> Approved
                                                            </div>
                                                        ) : (
                                                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                                                                <Loader2 className="h-3 w-3animate-spin" /> Pending
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            {!item.approvedFlg && (
                                                                <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-900/20" onClick={() => handleApprove(item.feedbackId)} title="Approve">
                                                                    <CheckCircle className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                            <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/20" onClick={() => handleDelete(item.feedbackId)} title="Delete">
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Mobile View: Cards Layout */}
                            <div className="md:hidden space-y-4">
                                {feedbacks.length === 0 ? (
                                    <div className="text-center py-10 text-muted-foreground bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 flex flex-col items-center gap-2">
                                        <MessageSquare className="h-10 w-10 text-slate-300" />
                                        <p>No feedback found.</p>
                                    </div>
                                ) : (
                                    feedbacks.map((item) => (
                                        <div key={item.feedbackId} className="bg-white dark:bg-slate-900 rounded-xl p-4 shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col gap-3 relative">
                                            {/* Header */}
                                            <div className="flex justify-between items-start">
                                                <div className="flex items-start gap-3">
                                                    <div className="h-10 w-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                                                        <User className="h-5 w-5 text-slate-500" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{item.customerName}</h4>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <div className="flex">
                                                                {[...Array(5)].map((_, i) => (
                                                                    <Star key={i} className={`h-3 w-3 ${i < item.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200 dark:text-slate-700'}`} />
                                                                ))}
                                                            </div>
                                                            <span className="text-[10px] text-muted-foreground">• {new Date(item.createdDate).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Actions Overlay */}
                                                <div className="flex gap-1">
                                                    {!item.approvedFlg && (
                                                        <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20" onClick={() => handleApprove(item.feedbackId)}>
                                                            <CheckCircle className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 bg-rose-50 dark:bg-rose-900/20" onClick={() => handleDelete(item.feedbackId)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Body */}
                                            <div className="bg-slate-50 dark:bg-slate-950/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800/50">
                                                <p className="text-sm text-slate-700 dark:text-slate-300 italic leading-relaxed">"{item.feedbackMessage}"</p>
                                            </div>

                                            {/* Footer */}
                                            <div className="flex justify-between items-center pt-2">
                                                {item.approvedFlg ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded">
                                                        <CheckCircle className="h-3 w-3" /> Approved
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">
                                                        <Loader2 className="h-3 w-3 animate-spin" /> Pending Review
                                                    </span>
                                                )}
                                                {item.customerEmail && (
                                                    <span className="text-xs text-muted-foreground truncate max-w-[150px]">{item.customerEmail}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Pagination Footer */}
                <div className="border-t bg-white dark:bg-slate-900 p-4 md:px-6 flex items-center justify-between shrink-0">
                    <span className="text-xs md:text-sm text-muted-foreground font-medium">
                        Page {pageNumber + 1} of {totalPages || 1}
                    </span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPageNumber(p => Math.max(0, p - 1))} disabled={pageNumber === 0} className="h-8 text-xs">
                            Previous
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setPageNumber(p => p + 1)} disabled={pageNumber >= totalPages - 1} className="h-8 text-xs">
                            Next
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default AdminFeedbackDashboard;
